import React, {useState, useEffect, useCallback} from "react";
import {ThemeProvider} from "styled-components";
import * as querystring from 'querystring';
import Loading from "./components/Loading";
import Menu from "./components/Menu";
import Main from "./container/Main";
import Layer from "./container/Layer";
import Home from "./views/Home/Home";
import List from "./views/List/List";
import Radar from "./views/Radar/Radar";
import Scatter from "./views/Scatter/Scatter";
import Tiles from "./views/Tiles/Tiles";
import Filter from "./container/Filter";
import Error from "./components/Error";
import Toast from "./components/Toast";
import Tooltip from "./components/Tooltip";
import EditRadar from './components/EditRadar';
import EditScatter from './components/EditScatter';
import Comparison from './components/Comparison';
import Recommended from './components/Recommended';
import GraphColor from './components/GraphColor';
import {scrollTo, toLabel, toNum, byProperty, units} from "./lib/utils";
import {get, post} from "./lib/api";
import {theme} from "./lib/theme";
import {dataRawStatic} from "./lib/dataRaw";
import {dataNormalizedStatic} from "./lib/dataNormalized";
import {dataClassifiedStatic} from "./lib/dataClassified";
import "./App.css";

export default function App() {
  const [environment, setEnvironment] = useState();
  const [user, setUser] = useState();
  const [view, setView] = useState();
  const [dataRaw, setRawData] = useState({}); // loaded/static product data with original values
  const [dataNormalized, setNormalizedData] = useState({}); // loaded/static product data with normalized values
  const [dataClassified, setClassifiedData] = useState({}); // loaded/static product data with classified values
  const [productData, setProductData] = useState([]);  // merged raw/nromalized/classified product data
  const [products, setProducts] = useState([]); // selected product ids
  const [benchmarks, setBenchmarks] = useState([]); // selected product ids
  const [propertiesData, setPropertiesData] = useState([]);  // all product properties that can be used as filter
  const [loading, setLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selection, setSelection] = useState(false); // flag to apply product selection
  const [toast, setToast] = useState(false); // flag to toggle product selection toast
  const [layer, setLayer] = useState();
  const [error, setError] = useState();
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");
  const [colors, setColors] = useState([]);
  const [compareProp, setCompareProp] = useState();
  const [comparison, setComparison] = useState([]); // comparison product-properties
  const [filterValues, setFilterValues] = useState([]); // customized product properties used as filter
  const [trackedValues, setTrackedValues] = useState([]);
  const [addedFilter, setAddedFilter] = useState(["InterfacialTension", "Spreadability", "SurfaceTension", "TiSiliconeOilFeel", "TiPowdery", "RoDistribution"]);
  const [scatterProps, setScatterProps] = useState(["Spreadability", "Interfacial Tension"]);
  const labels = ["low", "medium", "high", "very high"];
  const [radarProps, setRadarProps] = useState([
    // physical
    "AverageMolecularWeight", "Spreadability", "Viscosity", "SurfaceTension", "InterfacialTension", "RefractiveIndex",
    // sensory
    "Gliding", "RoCushiony", "RoDistribution", "RoOil", "Thickness", "TiPowdery", "TiResidue1", "TiResidue2",
    "TiSiliconeOilFeel", "TiWaxdry",
  ].sort(byProperty("key")));

  const getUser = useCallback(() => {
    setLoading(true);
    get(1).then(response => {
      if(response.IsLoggedIn){
        setUser(response);
      } else {
        if(environment === "dev"){
          setUser({
            Access_Token: "f437cdd6-278b-496e-b269-c7168fbbb731",
            Basket: null,
            CanUseOrderSample: true,
            Country: "France",
            Email: "patrick.brill.fr@basfqa.com",
            FederationLogin: true,
            Firstname: "Patrick",
            IsLoggedIn: true,
            LastUpdated: "\/Date(1569010226460+0200)\/",
            Lastname: "Brill-FR",
            LoginMessage: "successful",
            Region: "FRA",
            SessionID: "lr2jbzqsshmfh3gf1j401x41",
            UserId: "0a675dde-697a-44c5-b4b8-0822f6ac8247",
            UserRights: ["CanUseOrderSample"],
            WALogin: false
          })
        } else {
          setError("can't load user");
          setView("home");
        }
      }
      setLoading(false);
    }).catch(error => {
      console.error('Error during user load:', error);
    });
  }, [environment]);

  const getData = useCallback(() => {
    if(!error){
      // // FAKE
      // setRawData(dataRawStatic);
      // setNormalizedData(dataNormalizedStatic);
      // setClassifiedData(dataClassifiedStatic);
      // return;
      //console.log("getData");
      setLoading(true);
      // Load Raw Data
      get(2).then(response => setData(2, response, environment)).catch(error => {console.error('Error during data load:', error)});
      // Load Normalized Data
      get(3).then(response => setData(3, response, environment)).catch(error => {console.error('Error during data load:', error)});
      // Load Classified Data
      get(4).then(response => setData(4, response, environment)).catch(error => {console.error('Error during data load:', error)});
    }
  }, [environment, error]);

  const setData = (type, data, environment) => {
    if(data && (!data.Items || !data.Items.length)){
      // LOGGED OUT
      console.log("setData", environment)
      // if(environment === "dev"){
        // use static data
      if(environment === "dev"){
        console.log(`Static Data: ${type}`);
        switch(type){
          case 2:
            setRawData(dataRawStatic);
            break;
          case 3:
            setNormalizedData(dataNormalizedStatic);
            break;
          case 4:
            setClassifiedData(dataClassifiedStatic);
            break;
          default:
            break
        }
      } else {
        // Redirect to Home
        switch(type){
            case 2:
              setRawData([]);
              break;
            case 3:
              setNormalizedData([]);
              break;
            case 4:
              setClassifiedData([]);
              break;
            default:
              break
          }
        setError("can't load data");
        setView("home");
        setLoading(false);
      }
    } else {
      // LOGGED IN
      console.log(`Loaded Data: ${type}`);
      switch(type){
        case 2:
          setRawData(data);
          break;
        case 3:
          setNormalizedData(data);
          break;
        case 4:
          setClassifiedData(data);
          break;
        default:
          break
      }
    }
  }

  // Merge raw, normalized & classified data
  const getConvertedData = useCallback(() => {
    let index = 0;
    return dataRaw.Items.reduce((acc, curr) => {
      const others = ["Chemistry", "Naturalness", "INCI_Name"];
      const obj1 = {...curr.OtherData, ...curr.OtherData.RelatedProduct || {INCI_Name: curr["Title"]}, RelatedProduct: undefined}
      const obj2 = curr.PhysicalData;
      const obj3 = curr.SensoricData;
      const normalized = dataNormalized.Items[index];
      const classified = dataClassified.Items[index];
      const props = [];
      for (let key in obj1) {
        if (obj1.hasOwnProperty(key) && others.indexOf(key) > -1) {
          props.push({
            key: key,
            label: toLabel(key),
            value: key === "Naturalness" ? toNum(obj1[key], key) : obj1[key],
            normalized: toNum(normalized.OtherData[key]),
            classified: classified.OtherData[key],
            unit: units[key],
            type: key === "Naturalness" ? "physical" : "other"
          })
        }
      }
      for (let key in obj2) {
        if (obj2.hasOwnProperty(key)) {
          props.push({
            key: key,
            label: toLabel(key),
            value: toNum(obj2[key], key),
            normalized: toNum(normalized.PhysicalData[key]),
            classified: classified.PhysicalData[key],
            unit: units[key],
            type: "physical"
          })
        }
      }
      for (let key in obj3) {
        if (obj3.hasOwnProperty(key)) {
          props.push({
            key: key,
            label: toLabel(key),
            value: toNum(obj3[key]),
            normalized: toNum(normalized.SensoricData[key]),
            classified: classified.SensoricData[key],
            type: "sensory"
          })
        }
      }
      index++;
      let currProd = {
        id: obj1.PRD || obj1.Product_Name || curr.Title,
        name: (obj1.Product_Name || curr.Title).toUpperCase(),
        title: curr.Title,
        benchmark: obj1.Tags === "IsBenchmark",
        url: `${window.location.origin}/product-formulations/products/products-detail/${obj1.UrlName}`,
        properties: props,
        track: () => track({
          Type: "Navigation",
          Category: "Product Detail page",
          Action: "Link Click",
          Values: [{
            Prd: obj1.PRD || obj1.Product_Name || curr.Title,
            ProductName: obj1.Product_Name || curr.Title
          }]
        })
      };
      currProd.properties.forEach(currProp =>  {
        currProd[currProp.key] = currProp.value;
        }
      )
      return [...acc, currProd];
    }, []);
  }, [dataRaw, dataClassified, dataNormalized]);

  // Currrent Product list based on selection & filter
  const getCurrProducts = useCallback(() => {
      const arr = (products.length && selection && view !== "scatter")
      ? productData.filter(item => products.indexOf(item.id) > -1)
      : productData
      || [];
    const curr = filterValues.length > 0 ? arr.filter(item =>
      item.properties.filter(el => {
        if(el.type === "sensory")  {
          return filterValues.some(prop => prop.key === el.key
            && el.classified.toLowerCase() === labels[parseInt(prop.value/25)]
          )
        } else {
          return filterValues.some(prop => prop.key === el.key
            && el.value >= prop.value[0]
            && el.value <= prop.value[1]
          )
        }
      }
      ).length === filterValues.length
    ) : arr;
    //const currColors = selection ? curr.map((item, index) => ({...item, color: colors[item.key]}));
    return curr.map((item, index) => {
      const currCol = colors.filter(el => el.name === item.name)[0];
      return ({...item, color: currCol ? currCol.color : item.color})
    });
  }, [filterValues, productData, products, selection, colors, view]);

  // Properties with dynamic min/max values based on selected/filtered products
  const getCurrProperties = useCallback((data) => {
    if(!productData[0]){
      return propertiesData;
    }
    const getValueLimit = (curr, products, min) => {
      return products.reduce((num, obj) => {
        const currValue = obj.properties.filter(item => item.key === curr.key)[0].value;
        // const currKey= obj.properties.filter(item => item.key === curr.key)[0].key;
        // const value = currKey === "RefractiveIndex" && currValue === -1 ? 0 : currValue === -1 ? num : currValue;
        const value = currValue === -1 ? num : currValue;

        return (
          min
            ? (value < num ? value : num)
            : (value > num ? value : num)
        )}, curr.value);
    }
    const currProducts = getCurrProducts();
    return productData[0].properties
      .filter(item => item.key !== "INCI_Name" && item.key !== "Chemistry" && item.key !== "Naturalness")
      .reduce((acc, curr) => {
        const filteredProp = filterValues.filter(item => curr.key === item.key)[0];
          //if(curr.key === "RefractiveIndex" && !!filteredProp)console.log(filteredProp.value)
        return [...acc, {
          ...curr,
          minTotal: getValueLimit(curr, productData, true),
          minAvail: getValueLimit(curr, currProducts, true),
          min: filteredProp ? filteredProp.value[0] : getValueLimit(curr, productData, true),
          max: filteredProp ? filteredProp.value[1] : getValueLimit(curr, productData),
          value: filteredProp ? filteredProp.value : "",
          maxAvail: getValueLimit(curr, currProducts),
          maxTotal: getValueLimit(curr, productData),
        }
      ]}, [])
      .sort(byProperty("key"));
  }, [propertiesData, productData, filterValues, getCurrProducts]);

  // Handle all loaded data
  const completeData = useCallback(() => {
    if(products.length > 0){
      return;
    }
    const all = getConvertedData();
    const oils = all
      .filter((item => item.benchmark))
      .map(item => ({...item, color: "#000000"}));
    const productData = all
      .filter((item => !item.benchmark))
      .map((item, index) => ({key: index, ...item, color: colors[index]}));
    const propertiesData = getCurrProperties(productData);
    setBenchmarks(oils);
    setProductData(productData);
    setColors([
      {name: productData[0].name, color: theme.blueBright},
      {name: productData[1].name, color: theme.orange},
    ]);
    setPropertiesData(propertiesData);
    setLoading(false);
  }, [getConvertedData, getCurrProperties, products, colors]);

  // Add/Remove class="scrolled" to/from body
  const onScroll = (event) => {
    const scrollTop = (event.currentTarget || document.documentElement).scrollTop || window.pageYOffset;
    const classes = document.body.classList;
    if(scrollTop > 80 && !classes.contains("scrolled")){
      classes.add("scrolled");
      setScrolled(true);
    } else if(scrollTop <= 80 && classes.contains("scrolled")) {
      classes.remove("scrolled");
      setScrolled(false);
    }
  }

  // Sort Products
  const sortProducts = (key) => {
    const nextOrder = key !== sort ? "asc" : order === "asc" ? "desc": "asc";
    setOrder(nextOrder);
    setSort(key);
  }

  // De-/Select Products
  const toggleProducts = (curr) => {
    if(!!curr && Array.isArray(curr)) {
      // Apply
      scrollTo();
      setScrolled(false);
      setProducts(curr);
      setSelection(true);
      setToast(false);
    } else if(!!curr && typeof curr === "string") {
      // De-/Select single product
      const existing = products.filter(item => item === curr)[0];
      const arr = [existing ? null : curr, ...products.filter(item => item !== curr)].filter(item => item);
      setToast(!!arr.length && (view !== "home"));
      setSelection(false);
      setProducts(arr);
    } else {
      // Deselect all products
      setSelection(false);
      setProducts([]);
      setToast(false);
    }
  }

  // Apply customized property values to filter products
  const filterProducts = (data, value = null) => {
    if(data){
      const currValue = value && value[0] === data.minTotal && value[1] === data.maxTotal ? null : value;
      const currFilter = currValue
        ? [...filterValues.filter(item => item.key !== data.key), {key: data.key, value: currValue}]
        : filterValues.filter(item => item.key !== data.key);
      setFilterValues(currFilter);
    } else {
      setFilterValues([]);
    }
  }

  const toggleColor = (col) => {
    const newCol = col ? {name: layer.name, color: col} : null;
    const newColors = [...colors.filter(item => item.name !== layer.name), newCol].filter(item => item);
    setColors(newColors);
    setLayer();
    track({
      Type: "Action",
      Category: "Apply Color",
      Action: "Button Click",
      Values: [{
        Prd: layer.id,
        ProductName: layer.name
      }]
    });
  }

  const toggleCompareProp = (prop) => {
    const curr = prop === compareProp ? null : prop;
    setCompareProp(curr);
  }

  const toggleLayer = (str) => {
    const newLayer = layer === str ? "" : str;
    if(!!compareProp && !newLayer){
      setComparison([]);
      setCompareProp(null);
    }
    setLayer(newLayer);
  }

  const compareProps = () => {
    const items = currProducts.map(item => {
      const curr = item.properties.filter(prop => prop.label === compareProp)[0] || {value: "N/A"};
      return ({
        product: item.name,
        value: curr.type === "sensory" ? curr.classified : curr.value === -1 ? "N/A" : curr.value,
        unit: curr.unit
    })});
    setComparison(items);
    toggleLayer(compareProp);
  }

  const currProducts = view === "scatter" ? getCurrProducts().map(item =>
      item.properties.filter(prop => scatterProps.indexOf(prop.label) > -1 && (!prop.value || prop.value === -1)).length
      ? {...item, disabled: true}
      : item
    ) : getCurrProducts().sort(byProperty(sort, order)) ;
  const currProperties = getCurrProperties();

  const track = (obj) => {
    const payload = {
      FirstName: user.Firstname,
      LastName: user.Lastname,
      EmailAddress: user.Email,
      AccessToken: user.Access_Token,
      SessionId: user.SessionID,
      UserAgent: navigator.userAgent,
      UserLanguage: navigator.language,
      UserId: user.UserId,
      ...obj
    }

    post(5, payload).then(response => {
      //console.log(payload, response)
      if(response.data){

      } else {

      }
    }).catch(error => {
      console.error('Error during tracking:', error);
    });
  }

  useEffect(() => {
    if(!environment){
      const environment =
        window.location.href.indexOf("carecreations.basf.com") > -1 ? "prod"
        : window.location.href.indexOf("staging.aa-g.de") > -1 ? "stage"
        //: window.location.href.indexOf("rocket-dev.de") > -1 ? "stage"
        : "dev";
      document.addEventListener('scroll', onScroll);
      console.log("setEnvironment", environment);
      setEnvironment(environment);
    } else {
      const logged = !!window.auth_token && !!window.user_id;
      if(!view && !user && !loading && !error) {
        if(logged){
          console.log("get logged user");
          getUser();
        } else {
          console.log("not logged");
          setView("home");
        }
      }
      if(!view && user){
        console.log("setView: List");
        setView("list");
      }
      // if(!!view && !user && environment === "dev"){
      //   console.log("getData", environment);
      //   getUser();
      // }
      if(!!view && !!user && !productData.length && !loading && (!error || environment === "dev")){
        console.log("getData", environment);
        getData();
      }
      if(!productData.length && dataRaw.Items && dataNormalized.Items && dataClassified.Items) {
        console.log("completeData");
        completeData();
      }
    }

  }, [environment, loading, getUser, error, view, products, getData, productData, dataRaw, dataNormalized, dataClassified, completeData]);

  useEffect(() => {
    if(selection){
      track({
        Type: "Action",
        Category: "Product Filter Selection",
        Action: "Button Click",
        Values: currProducts.map(item => ({
          Prd: item.id,
          ProductName: item.name
        }))
      })
    }
  }, [selection]);

  useEffect(() => {
    if(user){
      track({
        Type: "Action",
        Category: "Apply Attributes",
        Action: "Button Click",
        Values: radarProps.map(item => ({
          AttributeName: item
        }))
      })
    }
  },  [radarProps]);

  useEffect(() => {
    if(user){
      track({
        Type: "Action",
        Category: "Apply Axis Attributes",
        Action: "Button Click",
        Values: scatterProps.map(item => ({
          AttributeName: item
        }))
      })
    }
  }, [scatterProps]);

  useEffect(() => {
    if(view && user){
      track({
        Type: "Navigation",
        Category: "View Selection",
        Action: "Button Click",
        Values:[{
          ViewName: view
        }]
      })
    }
  }, [view]);

  useEffect(() => {
    if(view && user){
      const values = currProperties
        .filter(item => addedFilter.indexOf(item.key) > -1)
        .reduce((acc, curr) => {
          const physical = curr.type === "physical";
          return ([...acc, {
            FilterName: curr.label,
            FilterValue: physical ? curr.value[0] || curr.minTotal : curr.value,
            IsMax: false,
            IsMin: physical,
            IsSingle: !physical,
          }, {
            FilterName: curr.label,
            FilterValue: physical ? curr.value[1] || curr.maxTotal : curr.value,
            IsMax: physical,
            IsMin: false,
            IsSingle: !physical,
          }])
        }
      , []);

      track({
        Type: "Action",
        Category: "Apply Filter Selection",
        Action: "Button Click",
        Values: values
      })
    }
  }, [addedFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if(view && user){
        const values = currProperties
          .filter(item => addedFilter.indexOf(item.key) > -1)
          .reduce((acc, curr) => {
            const physical = curr.type === "physical";
            if(physical && (curr.value[0] !== curr.minTotal || curr.value[1] !== curr.maxTotal)){
              return ([...acc, {
                FilterName: curr.label,
                FilterValue: curr.value[0],
                IsMax: false,
                IsMin: true,
                IsSingle: false,
              }, {
                FilterName: curr.label,
                FilterValue: curr.value[1],
                IsMax: true,
                IsMin: false,
                IsSingle: false,
              }]);
            } else if(!physical) {
              return ([...acc, {
                FilterName: curr.label,
                FilterValue: curr.value ? labels[parseInt(curr.value/25)] : null,
                IsMax: false,
                IsMin: false,
                IsSingle: true,
              }]);
            } else {
              return ({});
            }
          }
        , []).filter(item => !!item.FilterValue);

        if(values.length && JSON.stringify(values) !== JSON.stringify(trackedValues)){
          setTrackedValues(values);
          track({
            Type: "Action",
            Category: "Apply Filter Values",
            Action: "Button Click",
            Values: values
          })
        }
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [filterValues]);

  //if(dataNormalized && dataNormalized.Items)console.log("Normalized ",dataNormalized.Items[0]);

  return (
    <ThemeProvider theme={theme}>
      <div className="app">
        {/*<Header scrolled={scrolled} view={view} setView={setView}/>*/}
        <Main>
        {view === "home" &&
          <Home setView={setView} environment={environment}/>
        }
        {view === "list" &&
          <List
            items={currProducts}
            selected={products}
            toggleProducts={toggleProducts}
            sortProducts={sortProducts}
          />
        }
        {view === "tiles" &&
          <Tiles
            items={currProducts}
            selected={products}
            toggleProducts={toggleProducts}
            compareProp={compareProp}
            setCompareProp={toggleCompareProp}
          />
        }
        {view === "radar" &&
          <Radar
            scrolled={scrolled}
            benchmarks={benchmarks}
            items={currProducts}
            chartProps={radarProps}
            properties={currProperties}
            filter={filterValues}
            selected={products}
            toggleLayer={toggleLayer}
            toggleProducts={toggleProducts}
          />
        }
        {view === "scatter" &&
          <Scatter
            scrolled={scrolled}
            items={currProducts.filter(item => !item.disabled)}
            disabledItems={currProducts.filter(item => item.disabled)}
            chartProps={scatterProps}
            properties={currProperties}
            filter={filterValues}
            selected={products}
            toggleLayer={toggleLayer}
            toggleProducts={toggleProducts}
          />
        }
        </Main>
      {!!view && view !== "home" &&
        <Menu view={view} setView={setView}/>
      }
      {!!view && view !== "home" &&
        <Filter
          count={filterValues.length}
          scrolled={scrolled}
          properties={currProperties}
          setAdded={setAddedFilter}
          added={addedFilter}
          setFilter={filterProducts}
          selection={selection}
          products={products}
          toggleProducts={toggleProducts}
        />
      }
      {view !== "scatter" &&
        <Toast
          active={toast && !compareProp}
          confirm={() => {toggleProducts(products);}}
          cancel={toggleProducts}
          msg={`${products.length} ${products.length > 1 ? "Products" : "Product"}  selected`}
        />
      }

      {view === "tiles" &&
        <Toast
          active={!!compareProp}
          labelConfirm="compare"
          confirm={() => {compareProps();}}
          cancel={() => {setCompareProp(null);}}
          msg={`${compareProp} selected`}
        />
      }

        <Layer visible={!!layer || error} toggleLayer={toggleLayer}>
          {view === "tiles" && comparison.length ? (
            <Comparison
              prop={layer}
              data={comparison}
              toggleLayer={() => {toggleLayer(layer)}}
            />
          ) : layer === "radar" ? (
            <EditRadar
              properties={currProperties}
              chartProps={radarProps}
              setChartProps={setRadarProps}
              toggleLayer={() => toggleLayer(layer)}
            />
          ) : layer === "scatter" ? (
            <EditScatter
              properties={currProperties}
              chartProps={scatterProps}
              setChartProps={setScatterProps}
              toggleLayer={() => toggleLayer(layer)}
            />
          ) : (!!layer && layer.bm) ? (
            <Recommended
              productData={productData}
              bm={layer.bm}
              toggleLayer={() => toggleLayer(layer)}
            />
          ) : (!!layer && layer.name) ? (
            <GraphColor
              product={layer}
              toggleColor={toggleColor}
              toggleLayer={() => toggleLayer(layer)}
            />
          ) : (!!error) && (
            <Error
              error={error}
              toggleLayer={() => setError()}
            />
          )}
        </Layer>

        <Tooltip />
        <Loading overlay visible={loading} />

      </div>
    </ThemeProvider>
  );
}
